import React, { useMemo } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PS } from '../theme';
import { Wordmark } from '../lib/ui';
import { Icon } from '../lib/icons';
import { useSeo, SITE_ORIGIN } from '../lib/useSeo';
import GUIDE_ARTICLES from '../content/guideData';

const CLUSTER_LABEL = {
  A: 'พื้นฐานวงแชร์', B: 'สำหรับท้าวแชร์', C: 'กฎหมาย & ความน่าเชื่อถือ',
  D: 'เริ่มต้นตั้งวง', ToolHub: 'ตรวจหลักฐาน /verify', 'group-buy': 'แชร์ซื้อของ',
};

// Markdown → styled elements (consistent with the warm-ivory brand).
function mdComponents(nav) {
  const onLink = (e, href) => {
    if (href && (href.startsWith('/guide') || href.startsWith('/verify') || href.startsWith('/app') || href === '/')) {
      e.preventDefault();
      nav(href);
    }
  };
  return {
    h2: ({ children }) => <h2 style={{ fontSize: 'clamp(20px,3.2vw,25px)', fontWeight: 700, letterSpacing: -0.4, margin: '38px 0 14px', lineHeight: 1.3 }}>{children}</h2>,
    h3: ({ children }) => <h3 style={{ fontSize: 'clamp(16px,2.6vw,19px)', fontWeight: 600, margin: '26px 0 10px', lineHeight: 1.4 }}>{children}</h3>,
    p: ({ children }) => <p style={{ fontSize: 16, lineHeight: 1.85, margin: '0 0 16px', color: PS.text }}>{children}</p>,
    ul: ({ children }) => <ul style={{ margin: '0 0 18px', paddingLeft: 22, lineHeight: 1.8, fontSize: 16 }}>{children}</ul>,
    ol: ({ children }) => <ol style={{ margin: '0 0 18px', paddingLeft: 22, lineHeight: 1.8, fontSize: 16 }}>{children}</ol>,
    li: ({ children }) => <li style={{ margin: '0 0 7px' }}>{children}</li>,
    a: ({ href, children }) => <a href={href} onClick={(e) => onLink(e, href)} style={{ color: PS.terracotta, fontWeight: 600, textDecoration: 'none', borderBottom: `1px solid ${PS.terracotta}40` }}>{children}</a>,
    strong: ({ children }) => <strong style={{ fontWeight: 700, color: PS.ink2 }}>{children}</strong>,
    blockquote: ({ children }) => <blockquote style={{ margin: '20px 0', padding: '14px 18px', borderLeft: `3px solid ${PS.terracotta}`, background: PS.surface2, borderRadius: '0 10px 10px 0', fontSize: 15, color: PS.text }}>{children}</blockquote>,
    table: ({ children }) => <div style={{ overflowX: 'auto', margin: '0 0 20px' }}><table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14.5, minWidth: 340 }}>{children}</table></div>,
    thead: ({ children }) => <thead style={{ background: PS.panel }}>{children}</thead>,
    th: ({ children }) => <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 700, borderBottom: `1px solid ${PS.line}`, fontSize: 13.5 }}>{children}</th>,
    td: ({ children }) => <td style={{ padding: '10px 12px', borderBottom: `1px solid ${PS.line}`, lineHeight: 1.6, verticalAlign: 'top' }}>{children}</td>,
    hr: () => <hr style={{ border: 'none', borderTop: `1px solid ${PS.line}`, margin: '30px 0' }} />,
    code: ({ children }) => <code style={{ background: PS.panel, padding: '2px 6px', borderRadius: 5, fontFamily: PS.fontMono, fontSize: 13.5 }}>{children}</code>,
  };
}

export default function GuideArticle() {
  const { slug } = useParams();
  const nav = useNavigate();
  const article = useMemo(() => GUIDE_ARTICLES.find((a) => a.slug === slug), [slug]);

  const jsonLd = useMemo(() => {
    if (!article) return [];
    const url = `${SITE_ORIGIN}/guide/${article.slug}`;
    const out = [
      {
        '@context': 'https://schema.org', '@type': 'Article',
        headline: article.title, description: article.metaDescription,
        inLanguage: 'th-TH', mainEntityOfPage: url,
        author: { '@type': 'Organization', name: 'Peershare เปียแชร์' },
        publisher: { '@type': 'Organization', name: 'Peershare เปียแชร์', url: SITE_ORIGIN },
      },
      {
        '@context': 'https://schema.org', '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'หน้าแรก', item: SITE_ORIGIN },
          { '@type': 'ListItem', position: 2, name: 'คู่มือเล่นแชร์', item: `${SITE_ORIGIN}/guide` },
          { '@type': 'ListItem', position: 3, name: article.title, item: url },
        ],
      },
    ];
    if (article.faq && article.faq.length) {
      out.push({
        '@context': 'https://schema.org', '@type': 'FAQPage',
        mainEntity: article.faq.map((f) => ({
          '@type': 'Question', name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      });
    }
    return out;
  }, [article]);

  useSeo({
    title: article?.metaTitle,
    description: article?.metaDescription,
    path: `/guide/${slug}`,
    jsonLd,
  });

  if (!article) return <Navigate to="/guide" replace />;

  const components = mdComponents(nav);

  return (
    <div style={{ minHeight: '100vh', background: PS.page, color: PS.text, fontFamily: PS.fontUI }}>
      {/* Header */}
      <div style={{ borderBottom: `1px solid ${PS.line}`, background: PS.surface }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '16px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button data-testid="guide-home-btn" onClick={() => nav('/')} className="ps-press" style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}><Wordmark size={20} /></button>
          <button data-testid="guide-index-btn" onClick={() => nav('/guide')} className="ps-press" style={{ display: 'flex', gap: 6, alignItems: 'center', border: `1px solid ${PS.line}`, background: 'transparent', borderRadius: 999, padding: '7px 14px', fontSize: 12.5, fontWeight: 600, color: PS.text, fontFamily: PS.fontUI }}>
            <Icon.chevronL size={14} stroke={PS.ink} /> คู่มือทั้งหมด
          </button>
        </div>
      </div>

      <article style={{ maxWidth: 760, margin: '0 auto', padding: '34px 22px 70px' }}>
        {/* Breadcrumb */}
        <nav style={{ fontSize: 12, color: PS.textMuted, marginBottom: 16, display: 'flex', gap: 7, flexWrap: 'wrap' }}>
          <a href="/" onClick={(e) => { e.preventDefault(); nav('/'); }} style={{ color: PS.textMuted, textDecoration: 'none' }}>หน้าแรก</a><span>›</span>
          <a href="/guide" onClick={(e) => { e.preventDefault(); nav('/guide'); }} style={{ color: PS.textMuted, textDecoration: 'none' }}>คู่มือเล่นแชร์</a><span>›</span>
          <span style={{ color: PS.text }}>{CLUSTER_LABEL[article.cluster] || 'บทความ'}</span>
        </nav>

        <div style={{ fontSize: 11.5, fontWeight: 700, color: PS.terracotta, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 10 }}>{CLUSTER_LABEL[article.cluster] || 'คู่มือ'}</div>
        <h1 data-testid="guide-article-title" style={{ fontSize: 'clamp(26px,5vw,38px)', fontWeight: 700, letterSpacing: -0.8, lineHeight: 1.25, margin: '0 0 28px' }}>{article.title}</h1>

        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{article.body}</ReactMarkdown>

        {/* Standard conversion CTA */}
        <div style={{ marginTop: 40, padding: 24, borderRadius: 18, background: PS.ink, color: PS.page }}>
          <div style={{ fontSize: 19, fontWeight: 700, letterSpacing: -0.3 }}>เล่นแชร์อย่างมั่นใจ เพราะทุกอย่างมีหลักฐาน</div>
          <div style={{ fontSize: 13.5, opacity: 0.8, marginTop: 8, lineHeight: 1.6 }}>เปียแชร์เก็บทุกการจ่าย/เปียเป็นหลักฐานที่แก้ย้อนหลังไม่ได้ ฟรีตลอดไป ถูกต้องตาม พ.ร.บ. การเล่นแชร์ 2534 — เราไม่ถือเงินของคุณ</div>
          <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
            <button data-testid="guide-cta-create" onClick={() => nav('/app/create')} className="ps-press" style={{ padding: '0 20px', height: 44, background: PS.terracotta, color: '#fff', border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 700, fontFamily: PS.fontUI }}>ตั้งวงแชร์ฟรี</button>
            <button data-testid="guide-cta-verify" onClick={() => nav('/verify')} className="ps-press" style={{ padding: '0 20px', height: 44, background: 'rgba(245,241,234,0.14)', color: PS.page, border: `1px solid rgba(245,241,234,0.3)`, borderRadius: 12, fontSize: 14, fontWeight: 600, fontFamily: PS.fontUI }}>ตรวจซองหลักฐานที่ /verify</button>
          </div>
        </div>

        {/* Related (same cluster) */}
        <RelatedArticles current={article} onNav={nav} />
      </article>
      <GuideFooter nav={nav} />
    </div>
  );
}

function RelatedArticles({ current, onNav }) {
  const related = GUIDE_ARTICLES.filter((a) => a.slug !== current.slug && a.cluster === current.cluster).slice(0, 4);
  if (!related.length) return null;
  return (
    <div style={{ marginTop: 44 }}>
      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>บทความที่เกี่ยวข้อง</div>
      <div style={{ display: 'grid', gap: 10 }}>
        {related.map((a) => (
          <a key={a.slug} href={`/guide/${a.slug}`} data-testid={`related-${a.slug}`}
            onClick={(e) => { e.preventDefault(); onNav(`/guide/${a.slug}`); window.scrollTo(0, 0); }}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '14px 16px', background: PS.surface2, border: `1px solid ${PS.line}`, borderRadius: 12, textDecoration: 'none', color: PS.text }}>
            <span style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4 }}>{a.title}</span>
            <Icon.chevronR size={16} stroke={PS.textMuted} />
          </a>
        ))}
      </div>
    </div>
  );
}

export function GuideFooter({ nav }) {
  return (
    <footer style={{ borderTop: `1px solid ${PS.line}`, background: PS.surface }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '28px 22px', display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'space-between', alignItems: 'center' }}>
        <Wordmark size={18} />
        <div style={{ display: 'flex', gap: 18, fontSize: 13, flexWrap: 'wrap' }}>
          <a href="/guide" onClick={(e) => { e.preventDefault(); nav('/guide'); }} style={{ color: PS.text, textDecoration: 'none' }}>คู่มือเล่นแชร์</a>
          <a href="/verify" onClick={(e) => { e.preventDefault(); nav('/verify'); }} style={{ color: PS.text, textDecoration: 'none' }}>ตรวจหลักฐาน</a>
          <a href="/about" onClick={(e) => { e.preventDefault(); nav('/about'); }} style={{ color: PS.text, textDecoration: 'none' }}>เกี่ยวกับเรา</a>
        </div>
        <div style={{ fontSize: 11.5, color: PS.textMuted, width: '100%' }}>เปียแชร์ — คนกลางผู้เก็บหลักฐาน ไม่ถือเงิน ไม่ตัดสินข้อพิพาท · ถูกต้องตาม พ.ร.บ. การเล่นแชร์ พ.ศ. 2534</div>
      </div>
    </footer>
  );
}
